import type { BaseDocument } from '../mongo/base-document.interface';

/** VAT rate (%) a territory gets when none is set, and the one shown for territories stored before the field existed. */
export const DEFAULT_TERRITORY_VAT_RATE = 11;

export const TERRITORY_CURRENCY = {
  EUR: 'EUR',
  GBP: 'GBP',
  LBP: 'LBP',
  USD: 'USD',
} as const;

export type TerritoryCurrency = (typeof TERRITORY_CURRENCY)[keyof typeof TERRITORY_CURRENCY];

export const TERRITORY_LANGUAGE = {
  EN: 'en',
  FR: 'fr',
} as const;

export type TerritoryLanguage = (typeof TERRITORY_LANGUAGE)[keyof typeof TERRITORY_LANGUAGE];

export const TERRITORY_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
} as const;

export type TerritoryStatus = (typeof TERRITORY_STATUS)[keyof typeof TERRITORY_STATUS];

export const TERRITORY_ZONE_TYPE = {
  COUNTRY: 'country',
} as const;

export type TerritoryZoneType = (typeof TERRITORY_ZONE_TYPE)[keyof typeof TERRITORY_ZONE_TYPE];

export interface TerritoryZone {
  countries: string[];
  type: 'country';
}

/**
 * Full Territory entity as stored in MongoDB.
 * A Territory is an organizational unit within a Tenant, grouping Sites.
 */
export interface Territory extends BaseDocument {
  commercialRegisterNumber?: string;
  currency: TerritoryCurrency;
  defaultLanguage: TerritoryLanguage;
  description?: string;
  name: string;
  status: TerritoryStatus;
  taxIdentificationNumber?: string;
  /**
   * VAT rate as a percentage (0-100), applied to rent on new quotations. A quotation keeps the rate it was
   * priced with. Optional because territories stored before this field have none.
   */
  vatRate?: number;
  zone: TerritoryZone;
}

/**
 * Territory enriched with the aggregated count of sites belonging to it.
 */
export interface TerritoryWithLocationCount extends Territory {
  locationCount: number;
}

/**
 * Payload for creating a new Territory.
 * Omits BaseDocument fields.
 */
export type CreateTerritory = Omit<Territory, 'createdAt' | 'deletedAt' | 'id' | 'tenantId' | 'updatedAt'>;

/**
 * Payload for updating an existing Territory via PATCH.
 */
export interface UpdateTerritory {
  commercialRegisterNumber?: string;
  currency?: TerritoryCurrency;
  defaultLanguage?: TerritoryLanguage;
  description?: string;
  name?: string;
  status?: TerritoryStatus;
  taxIdentificationNumber?: string;
  /** VAT rate as a percentage (0-100), applied to rent on new quotations. */
  vatRate?: number;
  zone?: TerritoryZone;
}
